import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './TabLevelActions.css';

type ActionOption = {
  id: string;
  label: string;
  onClick?: () => void;
};

export type TabLevelCollapsibleAction = {
  id: string;
  label: string;
  icon: string;
  onClick?: () => void;
  options?: ActionOption[];
  toggleLabels?: readonly [string, string];
  alwaysInOverflow?: boolean;
};

export type TabLevelFixedAction = {
  id: string;
  label: string;
  icon?: string;
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
};

type TabLevelActionsProps = {
  fixedActions: TabLevelFixedAction[];
  collapsibleActions: TabLevelCollapsibleAction[];
  isFilterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
  filterPanelRef?: React.RefObject<HTMLElement | null>;
  onFilter?: () => void;
  filterLabel?: string;
  showFilterAction?: boolean;
};

const ACTIONS_GAP = 10;
const ACTION_GAP = 4;

export function TabLevelActions({
  fixedActions,
  collapsibleActions,
  isFilterOpen: isFilterOpenProp = false,
  onFilterOpenChange,
  filterPanelRef,
  onFilter,
  filterLabel = 'Filter (1)',
  showFilterAction = true,
}: TabLevelActionsProps) {
  const actionsAreaRef = useRef<HTMLDivElement>(null);
  const fixedActionsRef = useRef<HTMLDivElement>(null);
  const collapsibleActionsRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const overflowMeasureRef = useRef<HTMLButtonElement>(null);
  const actionMeasureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(
    () => collapsibleActions.filter((action) => !action.alwaysInOverflow).length
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openInlineDropdownId, setOpenInlineDropdownId] = useState<string | null>(null);
  const [openOverflowDropdownId, setOpenOverflowDropdownId] = useState<string | null>(null);
  const [selectedLabelsByAction, setSelectedLabelsByAction] = useState<Record<string, string>>({});
  const isFilterOpen = isFilterOpenProp;

  const displayedActions = useMemo(
    () =>
      collapsibleActions.map((action) => ({
        ...action,
        displayLabel: selectedLabelsByAction[action.id] ?? action.label,
      })),
    [collapsibleActions, selectedLabelsByAction]
  );
  const inlineCandidates = useMemo(
    () => displayedActions.filter((action) => !action.alwaysInOverflow),
    [displayedActions]
  );

  useLayoutEffect(() => {
    const calculateVisible = () => {
      const container = actionsAreaRef.current;
      const fixedActionsNode = fixedActionsRef.current;
      const overflowMeasure = overflowMeasureRef.current;
      if (!container || !fixedActionsNode || !overflowMeasure) return;

      const containerWidth = container.offsetWidth;
      const fixedWidth = fixedActionsNode.offsetWidth;
      const available = Math.max(0, containerWidth - fixedWidth - ACTIONS_GAP);
      const overflowWidth = overflowMeasure.offsetWidth;

      const widths = inlineCandidates.map(
        (_, index) => actionMeasureRefs.current[index]?.offsetWidth ?? 0
      );

      if (widths.length === 0) return;

      if (widths.some((width) => width <= 0)) {
        scheduleRecalc();
        return;
      }

      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i += 1) {
        const next = widths[i] + (count > 0 ? ACTION_GAP : 0);
        if (used + next <= available) {
          used += next;
          count += 1;
        } else {
          break;
        }
      }

      const hiddenCount = inlineCandidates.length - count;
      if (hiddenCount > 0) {
        let usedWithOverflow = used + (count > 0 ? ACTION_GAP : 0) + overflowWidth;
        while (count > 0 && usedWithOverflow > available) {
          const removedWidth = widths[count - 1];
          used -= removedWidth;
          if (count > 1) {
            used -= ACTION_GAP;
          }
          count -= 1;
          usedWithOverflow = used + (count > 0 ? ACTION_GAP : 0) + overflowWidth;
        }
      }

      setVisibleCount(Math.max(0, Math.min(count, inlineCandidates.length)));
    };

    const scheduleRecalc = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(calculateVisible);
      });
    };

    scheduleRecalc();

    const resizeObserver = new ResizeObserver(() => scheduleRecalc());
    if (rootRef.current) resizeObserver.observe(rootRef.current);
    if (actionsAreaRef.current) resizeObserver.observe(actionsAreaRef.current);
    if (fixedActionsRef.current) resizeObserver.observe(fixedActionsRef.current);
    if (collapsibleActionsRef.current) resizeObserver.observe(collapsibleActionsRef.current);

    window.addEventListener('resize', scheduleRecalc);
    window.addEventListener('orientationchange', scheduleRecalc);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => scheduleRecalc());
    }

    return () => {
      window.removeEventListener('resize', scheduleRecalc);
      window.removeEventListener('orientationchange', scheduleRecalc);
      resizeObserver.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [displayedActions, inlineCandidates, showFilterAction, filterLabel, fixedActions]);

  const visibleActions = useMemo(
    () => inlineCandidates.slice(0, visibleCount),
    [inlineCandidates, visibleCount]
  );
  const hiddenInlineActions = useMemo(
    () => inlineCandidates.slice(visibleCount),
    [inlineCandidates, visibleCount]
  );
  const hiddenInlineIds = useMemo(
    () => new Set(hiddenInlineActions.map((action) => action.id)),
    [hiddenInlineActions]
  );
  const collapsedActions = useMemo(
    () =>
      displayedActions.filter(
        (action) => action.alwaysInOverflow || hiddenInlineIds.has(action.id)
      ),
    [displayedActions, hiddenInlineIds]
  );
  const showOverflowMenu = collapsedActions.length > 0;

  useLayoutEffect(() => {
    const container = actionsAreaRef.current;
    const inline = collapsibleActionsRef.current?.querySelector('.tla-collapsible-inline');

    if (container && container.scrollWidth > container.clientWidth + 1 && visibleCount > 0) {
      setVisibleCount((previous) => Math.max(0, previous - 1));
      return;
    }

    if (inline instanceof HTMLElement && inline.scrollWidth > inline.clientWidth + 1 && visibleCount > 0) {
      setVisibleCount((previous) => Math.max(0, previous - 1));
    }
  }, [visibleCount, showOverflowMenu, showFilterAction, filterLabel]);

  useEffect(() => {
    if (!showOverflowMenu) {
      setIsMenuOpen(false);
    }
  }, [showOverflowMenu]);

  useEffect(() => {
    const onGlobalPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideRoot = rootRef.current?.contains(target);
      const clickedInsidePanel = filterPanelRef?.current?.contains(target);

      if (!clickedInsideRoot && !clickedInsidePanel) {
        setIsMenuOpen(false);
        setOpenInlineDropdownId(null);
        setOpenOverflowDropdownId(null);
        onFilterOpenChange?.(false);
      }
    };

    document.addEventListener('mousedown', onGlobalPointerDown);
    return () => document.removeEventListener('mousedown', onGlobalPointerDown);
  }, [filterPanelRef, onFilterOpenChange]);

  const handlePrimaryAction = (action: TabLevelCollapsibleAction, location: 'inline' | 'overflow') => {
    if (action.options && action.options.length > 0) {
      if (location === 'inline') {
        setOpenInlineDropdownId((prev) => (prev === action.id ? null : action.id));
        setOpenOverflowDropdownId(null);
      } else {
        setOpenOverflowDropdownId((prev) => (prev === action.id ? null : action.id));
        setOpenInlineDropdownId(null);
      }
      return;
    }

    if (action.toggleLabels) {
      setSelectedLabelsByAction((previous) => {
        const current = previous[action.id] ?? action.label;
        const toggleLabels = action.toggleLabels;
        if (!toggleLabels) {
          return previous;
        }
        const [firstLabel, secondLabel] = toggleLabels;
        const next = current === firstLabel ? secondLabel : firstLabel;
        return { ...previous, [action.id]: next };
      });
    }

    action.onClick?.();
    setIsMenuOpen(false);
    setOpenInlineDropdownId(null);
    setOpenOverflowDropdownId(null);
  };

  return (
    <div
      className={`tab-level-actions ${isFilterOpen ? 'tab-level-actions-filter-open' : ''}`}
      ref={rootRef}
    >
      <div className="tla-actions" ref={actionsAreaRef}>
        <div className="tla-fixed-actions" ref={fixedActionsRef}>
          {fixedActions.map((action) => (
            <button
              key={action.id}
              type="button"
              className={`tla-fixed-button ${
                action.variant === 'primary' ? 'tla-fixed-button-primary' : 'tla-fixed-button-secondary'
              }`}
              onClick={action.onClick}
            >
              {action.icon ? (
                <span className="material-symbols-outlined" aria-hidden="true">
                  {action.icon}
                </span>
              ) : null}
              <span>{action.label}</span>
            </button>
          ))}

          {showFilterAction && (
            <div className={`tla-filter-wrap ${isFilterOpen ? 'tla-filter-wrap-open' : ''}`}>
              <button
                type="button"
                className="tla-filter-button"
                aria-haspopup="dialog"
                aria-expanded={isFilterOpen}
                onClick={() => {
                  const next = !isFilterOpen;
                  onFilterOpenChange?.(next);
                  if (!isFilterOpen && next) onFilter?.();
                }}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  filter_list
                </span>
                {filterLabel}
              </button>
            </div>
          )}
        </div>

        <div className="tla-collapsible-actions" ref={collapsibleActionsRef}>
          <div className="tla-collapsible-inline">
            {visibleActions.map((action) => {
              const hasOptions = Boolean(action.options?.length);
              const isOpen = openInlineDropdownId === action.id;

              return (
                <div key={action.id} className="tla-action-with-menu">
                  <button
                    type="button"
                    className="tla-collapsible-button"
                    onClick={() => handlePrimaryAction(action, 'inline')}
                    title={action.label}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      {action.icon}
                    </span>
                    <span>{action.displayLabel}</span>
                  </button>

                  {hasOptions && isOpen && (
                    <div className="tla-inline-dropdown tla-animate-in" role="menu">
                      {action.options?.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className="tla-inline-dropdown-item"
                          role="menuitem"
                          onClick={() => {
                            setSelectedLabelsByAction((previous) => ({
                              ...previous,
                              [action.id]: option.label,
                            }));
                            option.onClick?.();
                            setOpenInlineDropdownId(null);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {showOverflowMenu && (
            <div className="tla-overflow-menu">
              <button
                type="button"
                className="tla-overflow-trigger"
                aria-label="More actions"
                aria-expanded={isMenuOpen}
                aria-haspopup="menu"
                onClick={() => setIsMenuOpen((prev) => !prev)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  more_vert
                </span>
              </button>

              {isMenuOpen && collapsedActions.length > 0 && (
                <div className="tla-overflow-list tla-animate-in" role="menu">
                  {collapsedActions.map((action) => (
                    <div key={action.id} className="tla-overflow-item-wrap">
                      <button
                        type="button"
                        className="tla-overflow-item"
                        role="menuitem"
                        onClick={() => handlePrimaryAction(action, 'overflow')}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          {action.icon}
                        </span>
                        <span>{action.displayLabel}</span>
                      </button>

                      {action.options &&
                        action.options.length > 0 &&
                        openOverflowDropdownId === action.id && (
                          <div className="tla-overflow-submenu tla-animate-in" role="menu">
                            {action.options.map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                className="tla-inline-dropdown-item"
                                role="menuitem"
                                onClick={() => {
                                  setSelectedLabelsByAction((previous) => ({
                                    ...previous,
                                    [action.id]: option.label,
                                  }));
                                  option.onClick?.();
                                  setOpenOverflowDropdownId(null);
                                  setIsMenuOpen(false);
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="tla-measurer" aria-hidden="true">
        {inlineCandidates.map((action, index) => (
          <button
            key={`${action.id}-measure`}
            type="button"
            className="tla-collapsible-button"
            ref={(element) => {
              actionMeasureRefs.current[index] = element;
            }}
          >
            <span className="material-symbols-outlined">{action.icon}</span>
            <span>{action.displayLabel}</span>
          </button>
        ))}
        <button type="button" className="tla-overflow-trigger" ref={overflowMeasureRef}>
          <span className="material-symbols-outlined">more_vert</span>
        </button>
      </div>
    </div>
  );
}
